import { Injectable, NgZone, inject, signal, WritableSignal } from '@angular/core';
import { PlatformService } from '@services/platform.service';
import { AppStateChangeService } from '@services/app-state-change.service';
import { NavController } from '@ionic/angular';
import ShareInbox, { ShareInboxLastTarget, ShareInboxNativePayload } from '@plugins/shareInbox/shareInbox.plugin';
import { SessionStorageService } from '@services/session-storage.service';
import { Router } from '@angular/router';
import { ShareInboxFile, ShareInboxPayload, ShareInboxSendRequest } from '@interfaces/share-inbox.interfaces';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const SESSION_PAYLOAD_KEY = 'shareInboxPayload';
const SESSION_SEND_KEY = 'shareInboxSendRequest';

@Injectable({
  providedIn: 'root'
})
export class ShareInboxService {
  private readonly platformService = inject(PlatformService);
  private readonly appStateChangeService = inject(AppStateChangeService);
  private readonly navController = inject(NavController);
  private readonly sessionStorage = inject(SessionStorageService);
  private readonly router = inject(Router);
  private readonly ngZone = inject(NgZone);

  private readonly payloadSignal: WritableSignal<ShareInboxPayload | null> = signal(null);
  private readonly sendRequestSignal: WritableSignal<ShareInboxSendRequest | null> = signal(null);
  private listenerAttached: boolean = false;
  public readonly payloadChanges = this.payloadSignal.asReadonly();

  public constructor() {
    this.restoreFromSession();
    this.attachNativeListener();
    this.subscribeToAppResume();
  }

  public get payload(): ShareInboxPayload | null {
    return this.payloadSignal();
  }

  public get hasPayload(): boolean {
    return !!this.payloadSignal();
  }

  public clearPayload(): void {
    this.payloadSignal.set(null);
    this.sessionStorage.removeItem(SESSION_PAYLOAD_KEY);
    void this.clearNativePayload();
  }

  public clearSendRequest(): void {
    this.sendRequestSignal.set(null);
    this.sessionStorage.removeItem(SESSION_SEND_KEY);
  }

  public stageSend(chatId: number, autoSend: boolean): void {
    const payload = this.payloadSignal();
    if (!payload) return;
    const request: ShareInboxSendRequest = {chatId, payload, autoSend};
    this.sendRequestSignal.set(request);
    this.sessionStorage.setItem(SESSION_SEND_KEY, request);
  }

  public consumeSend(chatId: number): ShareInboxSendRequest | null {
    const request = this.sendRequestSignal();
    if (!request || request.chatId !== chatId) return null;
    this.sendRequestSignal.set(null);
    this.sessionStorage.removeItem(SESSION_SEND_KEY);
    return request;
  }

  public async checkNativePendingShare(): Promise<void> {
    if (!this.shouldHandlePendingShareInApp()) return;
    if (!this.platformService.isDevice) return;
    if (!this.isShareInboxAvailable()) return;
    try {
      const result = await ShareInbox.getPendingShare();
      const payload = this.mapNativePayload(result?.payload ?? null);
      if (!payload) return;
      this.handleIncomingPayload(payload);
    } catch (error) {
      console.error('Share inbox: failed to read pending share', error);
    }
  }

  public async checkNativeLastShareTarget(): Promise<boolean> {
    if (!this.shouldHandleLastShareTarget()) return false;
    if (!this.platformService.isDevice) return false;
    if (!this.isShareInboxAvailable()) return false;
    if (typeof ShareInbox?.getLastShareTarget !== 'function') return false;
    try {
      const result = await ShareInbox.getLastShareTarget();
      const target = this.mapNativeLastTarget(result?.target ?? null);
      if (!target) return false;
      this.clearPayload();
      this.clearSendRequest();
      if (typeof ShareInbox?.clearLastShareTarget === 'function') {
        await ShareInbox.clearLastShareTarget();
      }
      await this.navigateToChat(target.chatId);
      return true;
    } catch (error) {
      console.error('Share inbox: failed to read last target', error);
      return false;
    }
  }

  private restoreFromSession(): void {
    if (!this.shouldHandlePendingShareInApp()) {
      this.sessionStorage.removeItem(SESSION_PAYLOAD_KEY);
      this.sessionStorage.removeItem(SESSION_SEND_KEY);
      this.payloadSignal.set(null);
      this.sendRequestSignal.set(null);
      return;
    }

    const storedPayload = this.sessionStorage.getItem(SESSION_PAYLOAD_KEY, true);
    const payload = this.mapStoredPayload(storedPayload);
    if (payload) {
      this.payloadSignal.set(payload);
    }

    const storedRequest = this.sessionStorage.getItem(SESSION_SEND_KEY, true);
    const request = this.mapStoredSendRequest(storedRequest);
    if (request) {
      this.sendRequestSignal.set(request);
    }
  }

  private subscribeToAppResume(): void {
    this.appStateChangeService.appStateChange$.pipe(
      takeUntilDestroyed(),
    ).subscribe((isActive: boolean): void => {
      if (!isActive) return;
      void this.handleAppActive();
    });
  }

  private async handleAppActive(): Promise<void> {
    const handled = await this.checkNativeLastShareTarget();
    if (handled) return;
    if (!this.shouldHandlePendingShareInApp()) return;
    await this.checkNativePendingShare();
  }

  private attachNativeListener(): void {
    if (this.listenerAttached) return;
    if (!this.shouldHandlePendingShareInApp()) return;
    if (!this.platformService.isDevice) return;
    if (!this.isShareInboxAvailable()) return;
    this.listenerAttached = true;

    ShareInbox.addListener('shareReceived', (event: { payload?: ShareInboxNativePayload | null }) => {
      const payload = this.mapNativePayload(event?.payload ?? null);
      if (!payload) return;
      this.ngZone.run((): void => {
        this.handleIncomingPayload(payload);
      });
    });
  }

  private handleIncomingPayload(payload: ShareInboxPayload): void {
    const current = this.payloadSignal();
    if (current?.id === payload.id) {
      return;
    }

    this.payloadSignal.set(payload);
    this.sessionStorage.setItem(SESSION_PAYLOAD_KEY, payload);
    void this.navigateToInbox();
  }

  private async navigateToInbox(): Promise<void> {
    if (this.platformService.isSSR) return;
    if (this.router.url.startsWith('/chats/share')) {
      return;
    }
    await this.navController.navigateForward('/chats/share', {animated: false});
  }

  private async navigateToChat(chatId: number): Promise<void> {
    if (this.platformService.isSSR) return;
    await this.navController.navigateForward(['/chats', chatId], {animated: false});
  }

  private isShareInboxAvailable(): boolean {
    return typeof ShareInbox?.getPendingShare === 'function';
  }

  private shouldHandlePendingShareInApp(): boolean {
    return !this.platformService.isIosApp;
  }

  private shouldHandleLastShareTarget(): boolean {
    return this.platformService.isIosApp;
  }

  private async clearNativePayload(): Promise<void> {
    if (!this.platformService.isDevice) return;
    if (!this.isShareInboxAvailable()) return;
    try {
      await ShareInbox.clearPendingShare();
    } catch (error) {
      console.error('Share inbox: failed to clear native payload', error);
    }
  }

  private mapNativePayload(payload: ShareInboxNativePayload | null): ShareInboxPayload | null {
    if (!payload) return null;
    const files = Array.isArray(payload.files) ? payload.files : [];
    return {
      id: String(payload.id ?? ''),
      createdAt: Number(payload.createdAt ?? Date.now()),
      text: typeof payload.text === 'string' ? payload.text : null,
      title: typeof payload.title === 'string' ? payload.title : null,
      files: files
        .map((file): ShareInboxFile | null => this.parseFile(file))
        .filter((file): file is ShareInboxFile => !!file),
    };
  }

  private mapStoredPayload(value: unknown): ShareInboxPayload | null {
    return this.parsePayload(value);
  }

  private mapStoredSendRequest(value: unknown): ShareInboxSendRequest | null {
    if (!this.isRecord(value)) return null;
    const chatId = this.getNumber(value, 'chatId');
    const autoSend = this.getBoolean(value, 'autoSend');
    const payload = this.parsePayload(value['payload']);
    if (chatId === null || autoSend === null || !payload) return null;
    return {chatId, autoSend, payload};
  }

  private parsePayload(value: unknown): ShareInboxPayload | null {
    if (!this.isRecord(value)) return null;
    const id = this.getString(value, 'id');
    const createdAt = this.getNumber(value, 'createdAt');
    if (!id || createdAt === null) return null;
    const text = this.getString(value, 'text');
    const title = this.getString(value, 'title');
    const files = this.parseFiles(value['files']);
    return {
      id,
      createdAt,
      text,
      title,
      files,
    };
  }

  private parseFiles(value: unknown): ShareInboxFile[] {
    if (!Array.isArray(value)) return [];
    return value
      .map((item): ShareInboxFile | null => this.parseFile(item))
      .filter((item): item is ShareInboxFile => !!item);
  }

  private parseFile(value: unknown): ShareInboxFile | null {
    if (!this.isRecord(value)) return null;
    const name = this.getString(value, 'name');
    const path = this.getString(value, 'path');
    if (!name || !path) return null;
    const mimeType = this.getString(value, 'mimeType');
    const size = this.getNumber(value, 'size');
    return {
      name,
      path,
      mimeType,
      size,
    };
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  private getString(value: Record<string, unknown>, key: string): string | null {
    const raw = value[key];
    return typeof raw === 'string' ? raw : null;
  }

  private getNumber(value: Record<string, unknown>, key: string): number | null {
    const raw = value[key];
    return typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
  }

  private getBoolean(value: Record<string, unknown>, key: string): boolean | null {
    const raw = value[key];
    return typeof raw === 'boolean' ? raw : null;
  }

  private mapNativeLastTarget(value: ShareInboxLastTarget | null): ShareInboxLastTarget | null {
    if (!this.isRecord(value)) return null;
    const chatId = this.getNumber(value, 'chatId');
    if (chatId === null || chatId <= 0) return null;
    const sentAt = this.getNumber(value, 'sentAt') ?? 0;
    return {chatId, sentAt};
  }
}
